import type { ErrorInfo } from "react";
import { logger } from "@/lib/logger";

interface GrafanaFaroApi {
  pushError: (
    error: Error,
    options?: {
      context?: Record<string, string>;
      stack?: string;
      type?: string;
      message?: string;
    }
  ) => void;
}

interface GrafanaFaro {
  api: GrafanaFaroApi;
}

/**
 * Error context metadata for comprehensive error tracking
 * Designed to map directly to Sentry when integrated
 */
export interface ErrorContext {
  /** Name of the component where the error originated */
  componentName: string;
  /** Platform where the error occurred */
  platform: 'mobile' | 'desktop';
  /** Unix timestamp when error occurred */
  timestamp: number;
  /** The actual error object */
  error: Error;
  /** React error info with component stack */
  errorInfo?: ErrorInfo;
  /** Current route/URL path */
  route?: string;
  /** Browser user agent string */
  userAgent?: string;
  /** Environment (production/development) */
  environment?: 'production' | 'development';
  // Future fields for Sentry integration:
  // userId?: string;
  // sessionId?: string;
  // releaseVersion?: string;
}

/**
 * Centralized error reporting service
 *
 * Handles logging errors to console and prepares data structure for Sentry integration.
 * All error boundaries should report through this service for consistent tracking.
 *
 * @example
 * ```ts
 * ErrorReportingService.reportError({
 *   componentName: 'DocumentView',
 *   platform: 'desktop',
 *   timestamp: Date.now(),
 *   error: new Error('Failed to render'),
 *   route: window.location.pathname,
 * });
 * ```
 */
export class ErrorReportingService {
  /**
   * Report an error with full context
   *
   * Currently logs to console. When Sentry is integrated, errors will be
   * sent to Sentry with appropriate tags and context.
   */
  static reportError(context: ErrorContext): void {
    // Always log full error details to console
    logger.error(context.componentName, 'Error caught by error boundary:', {
      error: context.error,
      platform: context.platform,
      route: context.route,
      timestamp: new Date(context.timestamp).toISOString(),
      componentStack: context.errorInfo?.componentStack,
      environment: context.environment,
    });

    ErrorReportingService.reportGrafanaError(context);

    // TODO: Send to Sentry when integrated
    // Example Sentry integration:
    // if (typeof window !== 'undefined' && window.Sentry) {
    //   window.Sentry.captureException(context.error, {
    //     tags: {
    //       platform: context.platform,
    //       component: context.componentName,
    //       environment: context.environment,
    //     },
    //     contexts: {
    //       errorBoundary: {
    //         componentStack: context.errorInfo?.componentStack,
    //         timestamp: context.timestamp,
    //         route: context.route,
    //       }
    //     },
    //     // Add user context when auth is available
    //     // user: {
    //     //   id: context.userId,
    //     // }
    //   });
    // }
  }

  /**
   * Detect platform based on window width
   * Uses same breakpoint as useIsMobile hook (768px)
   */
  static detectPlatform(): 'mobile' | 'desktop' {
    if (typeof window === 'undefined') {
      return 'desktop'; // SSR fallback
    }
    return window.innerWidth < 768 ? 'mobile' : 'desktop';
  }

  /**
   * Get current environment
   */
  static getEnvironment(): 'production' | 'development' {
    return import.meta.env.PROD ? 'production' : 'development';
  }

  private static reportGrafanaError(context: ErrorContext): void {
    const grafana = ErrorReportingService.getGrafanaFaro();
    if (!grafana) {
      return;
    }

    const metadata: Record<string, string> = {
      componentName: context.componentName,
      platform: context.platform,
      environment: context.environment || 'development',
    };

    if (context.route) {
      metadata.route = context.route;
    }

    grafana.api.pushError(context.error, {
      context: metadata,
      message: context.error.message,
      stack: context.errorInfo?.componentStack ?? undefined,
      type: 'error-boundary',
    });
  }

  private static getGrafanaFaro(): GrafanaFaro | null {
    if (typeof window === 'undefined') {
      return null;
    }

    const candidate = window as Window & { grafanaFaro?: GrafanaFaro };
    if (!candidate.grafanaFaro?.api?.pushError) {
      return null;
    }

    return candidate.grafanaFaro;
  }
}
